// biome-ignore-all lint/suspicious/noArrayIndexKey: the only flagged list (shelter picker) is built fresh from server data on each render and never mutated mid-render — index IS the stable identity.
import { useCallback, useEffect, useId, useState } from "react";
import { getConfigValue } from "../../data/portalConfig";
import { getProfileByIdResult } from "../../data/profiles";
import { listUserSheltersByUserId } from "../../data/userShelters";
import {
  ACCESS_LOCKOUT_KEY,
  isRoleLockedOut,
  normalizeAccessLockout,
} from "../../services/accessLockout";
import {
  CAPTCHA_PENDING_MESSAGE,
  CAPTCHA_SWITCH_KEY,
  captchaBlocksSubmit,
  captchaEnabled,
  captchaOptions,
  captchaSwitchedOn,
} from "../../services/captcha";
import { passwordResetRedirect } from "../../utils/appUrl";
import { logAudit, logAuthFailure } from "../../utils/auditLog";
import { ROLES } from "../../utils/enums";
import { isStaffEmailDomain, STAFF_EMAIL_DOMAIN } from "../../utils/loginMethod";
import {
  applyActiveShelterContext,
  attributedName,
  buildSessionUser,
  primaryShelterCode,
  resolveActiveShelterContext,
} from "../../utils/sessionUser";
import { getSupabaseClient } from "../../utils/supabase";
import { Btn, Card, TxtInput } from "../../utils/ui.jsx";
import { Turnstile } from "./Turnstile";

export function Login({ onLogin }) {
  const [email, setEmail] = useState("");
  const [pass, setPass] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [shelterOptions, setShelterOptions] = useState(null);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetErr, setResetErr] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  // Code entry (scanner-proof onboarding/reset — docs/auth-email-code-entry-plan.md).
  // Corporate mail scanners pre-fetch link tokens and consume them, so auth emails
  // now carry an 8-digit OTP code the user types here instead of clicking a link.
  // codeContext: "onboard" = reached via the "Have an access code?" link (welcome/
  // invite email → a magiclink OTP); "reset" = reached from Forgotten-password
  // (a recovery OTP). verifyAccessCode tries both types so either code works.
  const [codeMode, setCodeMode] = useState(false);
  const [codeContext, setCodeContext] = useState("onboard");
  const [codeEmail, setCodeEmail] = useState("");
  const [code, setCode] = useState("");
  const [codeErr, setCodeErr] = useState("");
  const emailId = useId();
  const passwordId = useId();
  const codeEmailId = useId();
  const codeId = useId();
  // Cloudflare Turnstile (services/captcha.ts). captchaToken holds the current
  // single-use token; bumping captchaKey tears the widget down + re-renders it to
  // mint a fresh one after an attempt consumes the old token. All inert when
  // VITE_TURNSTILE_SITE_KEY is unset (captchaEnabled === false).
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaKey, setCaptchaKey] = useState(0);
  const onCaptchaToken = useCallback((t) => setCaptchaToken(t || ""), []);
  const resetCaptcha = useCallback(() => {
    setCaptchaToken("");
    setCaptchaKey((k) => k + 1);
  }, []);
  // Runtime kill switch (System Setup > Access, via db-188 anon-read). Read the
  // switch BEFORE sign-in; only an explicit stored `false` suppresses the widget.
  // Fail-open: default false (not killed), so a missing key or a read failure keeps
  // the widget showing — pairs with the "turn Supabase enforcement off first" rule
  // so a stale value can never cause a lockout.
  const [captchaKilled, setCaptchaKilled] = useState(false);
  useEffect(() => {
    if (!captchaEnabled) return undefined; // no widget in this build anyway
    let alive = true;
    getConfigValue(CAPTCHA_SWITCH_KEY).then((v) => {
      if (alive && !captchaSwitchedOn(v)) setCaptchaKilled(true);
    });
    return () => {
      alive = false;
    };
  }, []);
  // The widget shows only when the site key is configured AND the switch is on.
  const showCaptcha = captchaEnabled && !captchaKilled;

  async function doReset() {
    const supabaseClient = getSupabaseClient();
    setResetErr("");
    // With CAPTCHA on, the reset-email endpoint requires a token; hold the user
    // on the form until the widget has produced one.
    if (captchaBlocksSubmit(showCaptcha, captchaToken)) {
      setResetErr(CAPTCHA_PENDING_MESSAGE);
      return;
    }
    setLoading(true);
    // Trim a pasted/typed stray space (email-validation-plan §6A) — Supabase already
    // ignores case, but a trailing space makes the address not match any account.
    const cleanResetEmail = (resetEmail || "").trim();
    const { error } = await supabaseClient.auth.resetPasswordForEmail(cleanResetEmail, {
      redirectTo: passwordResetRedirect(),
      ...captchaOptions(captchaToken),
    });
    // The token is now spent — mint a fresh one in case the user retries.
    resetCaptcha();
    setLoading(false);
    if (error) {
      setResetErr(error.message);
      return;
    }
    // The reset email now carries an 8-digit CODE (dashboard "Reset Password"
    // template), not a working link — corporate scanners consume link tokens.
    // Move the user to the code-entry screen to type it.
    setResetMode(false);
    setCode("");
    setCodeErr("");
    setCodeEmail(cleanResetEmail);
    setCodeContext("reset");
    setCodeMode(true);
  }

  // Verify a one-time access code (onboarding/invite OR password reset). Establishes
  // a Supabase session, then reloads to the SAME origin with ?reset=1 so the app's
  // proven recovery path takes over: the mount restore builds the session user AND
  // applies the deactivation / login-method / MFA gates, while detectRecoveryFromUrl
  // flips passwordRecovery → the existing "Set a new password" screen. Same-origin is
  // required — the Supabase session lives in this origin's localStorage, so a redirect
  // to the canonical domain would lose it. Trying both OTP types keeps it robust
  // whether the code came from a magiclink (onboarding) or recovery (reset) email.
  async function verifyAccessCode() {
    const supabaseClient = getSupabaseClient();
    const cleanEmail = (codeEmail || "").trim();
    const cleanCode = (code || "").replace(/\s+/g, "");
    if (!cleanEmail || !cleanCode) {
      setCodeErr("Enter your email and the code from your email.");
      return;
    }
    setCodeErr("");
    setLoading(true);
    // Try the context's expected type first, then the other as a safety net.
    const types = codeContext === "reset" ? ["recovery", "magiclink"] : ["magiclink", "recovery"];
    let ok = false;
    for (const type of types) {
      const { error } = await supabaseClient.auth.verifyOtp({
        email: cleanEmail,
        token: cleanCode,
        type,
      });
      if (!error) {
        ok = true;
        break;
      }
    }
    setLoading(false);
    if (!ok) {
      // No session exists on a failed code, so logAudit's RLS insert would be
      // silently denied — route through the db-175 SECURITY DEFINER RPC instead.
      logAuthFailure("access_code_failed", { email: cleanEmail });
      setCodeErr(
        "That code wasn't recognised. It may have expired or already been used. Ask a Zephyr admin to send you a new one."
      );
      return;
    }
    window.location.assign(`${window.location.origin}/?reset=1`);
  }

  // Google Workspace SSO is gated behind a build-time flag so the whole
  // domain-adaptive path stays INERT until the Google provider is configured and
  // switched on (docs/google-sso-plan.md §0). Off → today's password screen for
  // everyone. On → a staff-domain email greys out the password field and routes to
  // Google on "Sign In". `staffSso` recomputes each render from the typed email.
  const ssoEnabled = import.meta.env.VITE_GOOGLE_SSO === "true";
  const staffSso = ssoEnabled && isStaffEmailDomain(email);

  // Google Workspace SSO (docs/google-sso-plan.md §5). Redirects to Google, then
  // back to the portal; the restore path in portal.jsx assembles the session and
  // enforces the login-method / MFA rules. `hd` hints Google to the staff domain.
  // On success the browser navigates away, so nothing below runs — only an
  // immediate error returns here.
  async function signInWithGoogle() {
    const supabaseClient = getSupabaseClient();
    setErr("");
    setLoading(true);
    const { error } = await supabaseClient.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: window.location.origin,
        queryParams: { hd: STAFF_EMAIL_DOMAIN },
      },
    });
    if (error) {
      setErr(error.message);
      setLoading(false);
    }
  }

  async function go() {
    const supabaseClient = getSupabaseClient();
    setErr("");
    // With CAPTCHA on, sign-in requires a token; hold the user on the form until
    // the widget has produced one (in Managed mode this is usually instant).
    if (captchaBlocksSubmit(showCaptcha, captchaToken)) {
      setErr(CAPTCHA_PENDING_MESSAGE);
      return;
    }
    setLoading(true);
    try {
      // Trim a pasted/typed stray space (email-validation-plan §6A) — the password is
      // left exactly as typed.
      const { data, error } = await supabaseClient.auth.signInWithPassword({
        email: (email || "").trim(),
        password: pass,
        options: { ...captchaOptions(captchaToken) },
      });
      // The token is single-use and now spent — mint a fresh one so any retry or
      // in-form refusal (deactivated / SSO / lockout) can submit again.
      resetCaptcha();
      if (error) {
        // Audit failed login attempts so we can spot brute-force / credential
        // stuffing in production logs. A failed sign-in has no session, so
        // logAudit's RLS insert would be silently denied — route through the
        // db-175 SECURITY DEFINER RPC instead.
        logAuthFailure("login_failed", { email: email, reason: error.message });
        // A deactivated user is Auth-banned, so sign-in fails here with the raw
        // "User is banned" — show the friendly deactivation message instead.
        const banned = error.code === "user_banned" || /banned/i.test(error.message || "");
        setErr(banned ? "This user is no longer active." : error.message);
        setLoading(false);
        return;
      }

      // Stage 2: one auth user = one profile. Multi-shelter users have
      // multiple user_shelters rows and choose via PostLoginSelection.
      const singleRes = await getProfileByIdResult(data.user.id);
      if (singleRes.error || !singleRes.data) {
        setErr("No profile found. Contact Zephyr staff.");
        setLoading(false);
        return;
      }
      // A deactivated profile must not be allowed in, even if the Auth ban never
      // took (defense in depth — docs/account-shelter-lifecycle-plan.md Phase 3).
      if (singleRes.data.deactivated) {
        // Audit BEFORE signing out: the insert must go out under the live session
        // (audit_logs RLS requires user_id = auth.uid()). logAudit is fire-and-forget,
        // so its insert dispatches before signOut resets the client's auth header.
        logAudit("session_refused_deactivated", {
          user_id: data.user.id,
          email: email,
          via: "login",
        });
        await supabaseClient.auth.signOut();
        setErr("This user is no longer active.");
        setLoading(false);
        return;
      }
      // Google SSO (docs/google-sso-plan.md §0): a Zephyr Workspace email must sign
      // in via Google, never a password. Derived from the email DOMAIN (not a stored
      // setting) and gated by the flag — so when SSO is off this never fires and
      // password sign-in works exactly as today. Belt-and-braces: the UI already
      // greys out the password field + routes a staff email to Google on Sign In.
      if (staffSso) {
        // Audit BEFORE signing out (see session_refused_deactivated above).
        logAudit("login_method_rejected", { email: email, method: "google", via: "password" });
        await supabaseClient.auth.signOut();
        setErr("Zephyr staff sign in with Google Workspace.");
        setLoading(false);
        return;
      }
      await finishLogin(data.user, singleRes.data);
    } catch (_e) {
      setErr("Login failed. Please try again.");
    }
    setLoading(false);
  }

  async function finishLogin(authUser, profile) {
    // Access lockout (Manage → System Setup → Access): a SuperUser can lock whole
    // role groups out of the portal. Refuse a locked-out role here, before we build
    // the session — with the configurable message. SuperUsers are never locked out.
    const lockout = normalizeAccessLockout(await getConfigValue(ACCESS_LOCKOUT_KEY));
    if (isRoleLockedOut(profile.role, lockout)) {
      // Audit BEFORE signing out (see session_refused_deactivated in go()).
      logAudit("access_lockout_blocked", {
        user_id: authUser.id,
        email: authUser.email,
        role: profile.role || "",
        via: "login",
      });
      await getSupabaseClient().auth.signOut();
      setErr(lockout.message);
      setLoading(false);
      return;
    }
    // Audit successful login — this is the point where the user has both
    // valid credentials AND a matching profile, so they're actually in.
    logAudit("login_success", {
      user_id: authUser.id,
      email: authUser.email,
      role: profile.role || "",
    });
    // Single source of truth (docs/retire-profile-shelter-code-plan.md Phase 2):
    // shelter membership comes ONLY from user_shelters; the primary shelter is the
    // is_primary membership. profiles.shelter_code is no longer read.
    const usRows = await listUserSheltersByUserId(authUser.id);
    const shelterCodes = usRows && usRows.length > 0 ? usRows.map((s) => s.shelter_code) : [];
    const primaryCode = primaryShelterCode(usRows);

    // A shelter caseworker with no membership has no shelter access - refuse the
    // session loudly (mirrors the deactivated refusal in go()). Internal roles
    // (staff/picker/combined/admin) legitimately have no shelter, never refused.
    if (profile.role === ROLES.SHELTER && shelterCodes.length === 0) {
      // Audit BEFORE signing out (see session_refused_deactivated in go()).
      logAudit("session_refused_no_shelter", {
        user_id: authUser.id,
        email: authUser.email,
        via: "login",
      });
      await getSupabaseClient().auth.signOut();
      setErr("This account isn't linked to a DFV Service. Please contact an administrator.");
      setLoading(false);
      return;
    }

    // A MULTI-shelter user goes through PostLoginSelection, which rebuilds the full
    // active-shelter context (state/address/delivery/contact) from the SELECTED
    // shelter via resolveActiveShelterContext. So don't derive any shelter-specific
    // context here for them — pass activeId=null for a neutral context; the chooser
    // fills it. A single-shelter (or no-shelter internal) user has nothing to
    // choose, so resolve their one membership now. This is THE reason the "state
    // stuck on the primary shelter" bug is gone: no per-shelter field is derived
    // here that the chooser could then fail to overwrite.
    const needsShelterChoice = shelterCodes.length > 1;
    const activeMembership = needsShelterChoice
      ? null
      : (usRows || []).find((m) => m.is_primary) || (usRows || [])[0] || null;
    const activeId = activeMembership?.shelter_id || null;
    const ctx = await resolveActiveShelterContext(
      activeId,
      activeMembership,
      profile.role === ROLES.SHELTER
    );

    // Assemble the base session user via the SAME builder the restore path uses
    // (buildSessionUser) — ONE assembler, so a field added to SessionUser is set on both
    // the login and restore paths or neither, ending the two-hand-built-lists drift
    // (docs/consolidate-login-buildsessionuser-plan.md). No shelterRow is passed:
    // buildSessionUser returns the base object with an EMPTY active-shelter context,
    // which applyActiveShelterContext then fills from ctx (the resolved ACTIVE shelter).
    // firstName/lastName ride on ctx so a multi-service caseworker's name follows the
    // chosen service, and are NEVER read from profiles.staff_* (the wall; 6 Aug 2026).
    const loginUser = applyActiveShelterContext(
      buildSessionUser({ authUser, profile, userShelters: usRows || [] }),
      ctx
    );
    // shelterCode + shelterName keep the fallbacks the context can't express: ctx's
    // shelterCode is "" for a pre-chooser multi-shelter user. shelterName goes through
    // attributedName() — the SAME helper buildSessionUser uses — so a shelter caseworker gets
    // their org name and a Zephyr staff user gets their short "Display Name" (preferred_name),
    // never the full name. (ctx.shelterName is "" for staff, so the staff branch runs.)
    loginUser.shelterCode = primaryCode || "";
    loginUser.shelterName = attributedName(ctx.shelterName, profile, authUser.email);
    onLogin(loginUser);
  }

  if (codeMode) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: `linear-gradient(135deg,${"#00695C"} 0%,${"#00897B"} 60%,#4DB6AC 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
        }}
      >
        <div style={{ width: "100%", maxWidth: 420 }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h1 style={{ color: "#FFFFFF", margin: 0, fontSize: 24, fontWeight: 700 }}>
              Zephyr Education Limited
            </h1>
          </div>
          <Card style={{ padding: 30 }}>
            <h2 style={{ margin: "0 0 6px", fontSize: 18, color: "#1A2E2B" }}>
              Enter your access code
            </h2>
            <p style={{ fontSize: 13, color: "#607D79", marginBottom: 18 }}>
              {codeContext === "reset"
                ? "We've emailed you an 8-digit code. Enter it below to set a new password."
                : "Enter the email address and 8-digit code from your Zephyr welcome email to set your password."}
            </p>
            <div style={{ marginBottom: 14 }}>
              <label
                htmlFor={codeEmailId}
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1A2E2B",
                  marginBottom: 5,
                }}
              >
                Email Address <span style={{ color: "#C62828" }}>*</span>
              </label>
              <input
                id={codeEmailId}
                type="email"
                value={codeEmail}
                onChange={function (e) {
                  setCodeEmail(e.target.value);
                }}
                placeholder="you@organisation.com.au"
                autoComplete="username"
                style={{
                  width: "100%",
                  padding: "9px 13px",
                  borderRadius: 7,
                  border: `1px solid ${"#CFE0DE"}`,
                  fontSize: 14,
                  color: "#1A2E2B",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  background: "#FFFFFF",
                }}
              />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label
                htmlFor={codeId}
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1A2E2B",
                  marginBottom: 5,
                }}
              >
                Access Code <span style={{ color: "#C62828" }}>*</span>
              </label>
              <input
                id={codeId}
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={function (e) {
                  setCode(e.target.value);
                }}
                onKeyDown={function (e) {
                  if (e.key === "Enter") verifyAccessCode();
                }}
                placeholder="12345678"
                style={{
                  width: "100%",
                  padding: "9px 13px",
                  borderRadius: 7,
                  border: `1px solid ${"#CFE0DE"}`,
                  fontSize: 18,
                  letterSpacing: 4,
                  color: "#1A2E2B",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                  background: "#FFFFFF",
                }}
              />
            </div>
            {codeErr && (
              <div
                style={{
                  background: "#FFEBEE",
                  border: "1px solid #f0000030",
                  borderRadius: 8,
                  padding: "9px 13px",
                  marginBottom: 14,
                  fontSize: 13,
                  color: "#C62828",
                }}
              >
                {codeErr}
              </div>
            )}
            <Btn
              onClick={verifyAccessCode}
              disabled={loading}
              style={{ width: "100%", padding: "12px 22px", fontSize: 15 }}
            >
              {loading ? "Verifying..." : "Continue ->"}
            </Btn>
            <p style={{ textAlign: "center", marginTop: 14, fontSize: 13, color: "#607D79" }}>
              <button
                type="button"
                onClick={() => {
                  setCodeMode(false);
                  setCode("");
                  setCodeErr("");
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                  color: "#00897B",
                  font: "inherit",
                  textDecoration: "underline",
                }}
              >
                Back to Sign In
              </button>
            </p>
          </Card>
        </div>
      </div>
    );
  }

  if (shelterOptions) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: `linear-gradient(135deg,${"#00695C"} 0%,${"#00897B"} 60%,#4DB6AC 100%)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 20,
          fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
        }}
      >
        <div style={{ width: "100%", maxWidth: 460 }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <h1 style={{ color: "#FFFFFF", margin: 0, fontSize: 24, fontWeight: 700 }}>
              Zephyr Education Limited
            </h1>
            <p style={{ color: "rgba(255,255,255,0.85)", margin: "6px 0 0", fontSize: 14 }}>
              Select which DFV Service you are working with today
            </p>
          </div>
          <Card style={{ padding: 28 }}>
            <h2 style={{ margin: "0 0 6px", fontSize: 17, color: "#1A2E2B" }}>
              Choose DFV Service
            </h2>
            <p style={{ fontSize: 13, color: "#607D79", marginBottom: 20 }}>
              Your login is linked to multiple DFV Services. Please select one to continue.
            </p>
            {shelterOptions.profiles.map(function (p, i) {
              const _supabaseClient = getSupabaseClient();
              return (
                <button
                  key={i}
                  onClick={function () {
                    finishLogin(shelterOptions.authUser, p);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "14px 18px",
                    marginBottom: 10,
                    border: `1.5px solid ${"#CFE0DE"}`,
                    borderRadius: 10,
                    background: "#FFFFFF",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "border-color 0.15s",
                  }}
                  onMouseOver={function (e) {
                    e.currentTarget.style.borderColor = "#00897B";
                  }}
                  onMouseOut={function (e) {
                    e.currentTarget.style.borderColor = "#CFE0DE";
                  }}
                  onFocus={function (e) {
                    e.currentTarget.style.borderColor = "#00897B";
                  }}
                  onBlur={function (e) {
                    e.currentTarget.style.borderColor = "#CFE0DE";
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#00695C" }}>
                    {p.shelter_name || p.id}
                  </div>
                  <div style={{ fontSize: 12, color: "#607D79", marginTop: 3 }}>
                    {p.state || ""}
                    {p.shelter_address ? ` \u2014 ${p.shelter_address}` : ""}
                  </div>
                </button>
              );
            })}
            <button
              onClick={function () {
                const supabaseClient = getSupabaseClient();
                // shelterOptions.authUser holds the email pre-signout, so log
                // before clearing the session.
                logAudit("logout", {
                  user_id: shelterOptions?.authUser?.id || "",
                  email: shelterOptions?.authUser?.email || "",
                  from: "shelter_picker",
                });
                setShelterOptions(null);
                supabaseClient.auth.signOut();
              }}
              style={{
                background: "none",
                border: "none",
                color: "#607D79",
                fontSize: 13,
                cursor: "pointer",
                fontFamily: "inherit",
                marginTop: 6,
                padding: 0,
              }}
            >
              Not you? Sign in with a different account
            </button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background: `linear-gradient(135deg,${"#00695C"} 0%,${"#00897B"} 60%,#4DB6AC 100%)`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "system-ui,-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif",
      }}
    >
      <div style={{ width: "100%", maxWidth: 420 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <img
            src="/zephyr-favicon-512.png"
            alt="Zephyr Education"
            width={84}
            height={84}
            style={{
              display: "inline-block",
              borderRadius: 18,
              border: "2px solid rgba(255,255,255,0.9)",
              boxSizing: "border-box",
              marginBottom: 14,
              boxShadow: "0 6px 22px rgba(0,0,0,0.25)",
            }}
          />
          <h1 style={{ color: "#FFFFFF", margin: 0, fontSize: 26, fontWeight: 700 }}>
            Zephyr Education Limited
          </h1>
          <p style={{ color: "rgba(255,255,255,0.8)", margin: "6px 0 0", fontSize: 14 }}>
            Zephyr DFV Services Request Portal
          </p>
        </div>
        <Card style={{ padding: 30 }}>
          {resetMode ? (
            <div>
              <h2 style={{ margin: "0 0 6px", fontSize: 18, color: "#1A2E2B" }}>Reset Password</h2>
              <p style={{ fontSize: 13, color: "#607D79", marginBottom: 18 }}>
                Enter your email address and we'll email you an 8-digit code to set a new password.
              </p>
              <TxtInput
                label="Email Address"
                type="email"
                value={resetEmail}
                onChange={setResetEmail}
                placeholder="you@organisation.com.au"
                required
              />
              {resetErr && (
                <div
                  style={{
                    background: "#FFEBEE",
                    border: "1px solid #f0000030",
                    borderRadius: 8,
                    padding: "9px 13px",
                    marginBottom: 14,
                    fontSize: 13,
                    color: "#C62828",
                  }}
                >
                  {resetErr}
                </div>
              )}
              {showCaptcha && <Turnstile onToken={onCaptchaToken} resetKey={captchaKey} />}
              <Btn
                onClick={doReset}
                disabled={loading}
                style={{ width: "100%", padding: "12px 22px", fontSize: 15 }}
              >
                {loading ? "Sending..." : "Send Code"}
              </Btn>
              <p style={{ textAlign: "center", marginTop: 14, fontSize: 13, color: "#607D79" }}>
                <button
                  type="button"
                  onClick={() => setResetMode(false)}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "#00897B",
                    font: "inherit",
                    textDecoration: "underline",
                  }}
                >
                  Back to Sign In
                </button>
              </p>
            </div>
          ) : (
            <div>
              <h2 style={{ margin: "0 0 22px", fontSize: 18, color: "#1A2E2B" }}>Sign In</h2>
              <div style={{ marginBottom: 14 }}>
                <label
                  htmlFor={emailId}
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1A2E2B",
                    marginBottom: 5,
                  }}
                >
                  Email Address <span style={{ color: "#C62828" }}>*</span>
                </label>
                <input
                  id={emailId}
                  type="email"
                  value={email}
                  onChange={function (e) {
                    setEmail(e.target.value);
                  }}
                  placeholder="you@organisation.com.au"
                  autoComplete={rememberMe ? "username" : "off"}
                  style={{
                    width: "100%",
                    padding: "9px 13px",
                    borderRadius: 7,
                    border: `1px solid ${"#CFE0DE"}`,
                    fontSize: 14,
                    color: "#1A2E2B",
                    fontFamily: "inherit",
                    boxSizing: "border-box",
                    background: "#FFFFFF",
                  }}
                />
              </div>
              <div style={{ marginBottom: 14 }}>
                <label
                  htmlFor={passwordId}
                  style={{
                    display: "block",
                    fontSize: 13,
                    fontWeight: 600,
                    color: "#1A2E2B",
                    marginBottom: 5,
                  }}
                >
                  Password <span style={{ color: "#C62828" }}>*</span>
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    id={passwordId}
                    type={showPass ? "text" : "password"}
                    value={pass}
                    onChange={function (e) {
                      setPass(e.target.value);
                    }}
                    onKeyDown={function (e) {
                      if (e.key === "Enter") staffSso ? signInWithGoogle() : go();
                    }}
                    disabled={staffSso}
                    placeholder={staffSso ? "Not needed — sign in with Google" : "********"}
                    autoComplete={rememberMe ? "current-password" : "off"}
                    style={{
                      width: "100%",
                      padding: "9px 42px 9px 13px",
                      borderRadius: 7,
                      border: `1px solid ${"#CFE0DE"}`,
                      fontSize: 14,
                      color: staffSso ? "#90A4A0" : "#1A2E2B",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                      background: staffSso ? "#EEF3F2" : "#FFFFFF",
                      cursor: staffSso ? "not-allowed" : "text",
                    }}
                  />
                  <button
                    onClick={function () {
                      setShowPass(function (v) {
                        return !v;
                      });
                    }}
                    type="button"
                    style={{
                      position: "absolute",
                      right: 10,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#607D79",
                      fontSize: 16,
                      padding: "2px 4px",
                      lineHeight: 1,
                    }}
                  >
                    {showPass ? "hide" : "show"}
                  </button>
                </div>
                {staffSso && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#607D79" }}>
                    Zephyr staff sign in with Google Workspace — just hit <strong>Sign In</strong>,
                    no password needed.
                  </div>
                )}
              </div>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 16,
                  fontSize: 13,
                  color: "#607D79",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={function (e) {
                    setRememberMe(e.target.checked);
                  }}
                />
                Remember me
              </label>
              {!staffSso && showCaptcha && (
                <Turnstile onToken={onCaptchaToken} resetKey={captchaKey} />
              )}
              {err && (
                <div
                  style={{
                    background: "#FFEBEE",
                    border: "1px solid #f0000030",
                    borderRadius: 8,
                    padding: "9px 13px",
                    marginBottom: 14,
                    fontSize: 13,
                    color: "#C62828",
                  }}
                >
                  {err}
                </div>
              )}
              <Btn
                onClick={staffSso ? signInWithGoogle : go}
                disabled={loading}
                style={{ width: "100%", padding: "12px 22px", fontSize: 15 }}
              >
                {loading ? "Signing in..." : "Sign In ->"}
              </Btn>
              <p style={{ textAlign: "center", marginTop: 18, fontSize: 13, color: "#607D79" }}>
                <button
                  type="button"
                  onClick={() => {
                    setResetMode(true);
                    setResetEmail(email);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "#00897B",
                    font: "inherit",
                    textDecoration: "underline",
                  }}
                >
                  Forgotten your password?
                </button>
              </p>
              <p style={{ textAlign: "center", marginTop: 8, fontSize: 13, color: "#607D79" }}>
                <button
                  type="button"
                  onClick={() => {
                    setCodeContext("onboard");
                    setCodeEmail(email);
                    setCode("");
                    setCodeErr("");
                    setCodeMode(true);
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    cursor: "pointer",
                    color: "#00897B",
                    font: "inherit",
                    fontWeight: 700,
                    textDecoration: "underline",
                  }}
                >
                  Have an access code?
                </button>
              </p>
              <div
                style={{
                  marginTop: 14,
                  fontSize: 12,
                  color: "#90A4A0",
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "center",
                  columnGap: 6,
                  rowGap: 0,
                  lineHeight: 1.2,
                }}
              >
                <span>This portal works best in Google Chrome</span>
                <svg viewBox="0 0 48 48" width="45" height="45" aria-hidden="true">
                  <path
                    fill="#4CAF50"
                    d="M44,24c0,11.045-8.955,20-20,20S4,35.045,4,24S12.955,4,24,4S44,12.955,44,24z"
                  />
                  <path
                    fill="#FFC107"
                    d="M24,4v20l8,4l-8.933,15.999C23.378,43.999,23.688,44,24,44c11.045,0,20-8.955,20-20c0-3.667-0.994-7.099-2.716-10.049L24,4z"
                  />
                  <path
                    fill="#F44336"
                    d="M41.284,13.951L24,24l-8-4L4.716,13.951C8.203,7.996,14.63,4,22,4c0.001,0,0.001,0,0.002,0L41.284,13.951z"
                  />
                  <path
                    fill="#2196F3"
                    d="M32,24c0,4.418-3.582,8-8,8s-8-3.582-8-8s3.582-8,8-8S32,19.582,32,24z"
                  />
                  <path
                    fill="#FFFFFF"
                    d="M24,17c-3.866,0-7,3.134-7,7s3.134,7,7,7s7-3.134,7-7S27.866,17,24,17z M24,29c-2.761,0-5-2.239-5-5s2.239-5,5-5s5,2.239,5,5S26.761,29,24,29z"
                  />
                </svg>
                <span>on a computer</span>
              </div>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
